import React, { useEffect, useMemo, useState } from 'react';
import { rh as api } from '../services/api';
import { nb, eur, montant, eurCourt, pct1, moisCourt, useFiltresVentes, PanneauVentes, usePanneau, SelecteurUnite } from './ventesUtils';
import './Ventes.css';
import './Stock.css';
import './VentesJour.css';
import './RhMasseSalariale.css';

// ─── RH — Masse salariale (Auto Antilles) ───────────────────────────────────
// Paie agrégée par entité × mois × activité (A97 DATA, recopiée de Finance).
// Seule donnée personnelle : la liste des collaborateurs (noms, SANS montant),
// chargée à la demande quand une concession est retenue — cf. Collaborateurs.
//
// Deux niveaux, comme les autres rapports en maille « Concessions groupées » :
// BLUE AUTOMOBILES, CENTRAUTODIS et SOGUAVASOREC, chacune dépliable sur ses
// sociétés de paie. CENTRAUTODIS en a trois : Autodis, Centre Auto et le GIE
// (personnel partagé, sans vente propre — ses ratios sont vides). Comme VN —
// Performance (2026-10-28, demande du user, qui raisonne en concession
// groupée) : un clic sur la ligne d'un groupe filtre sur toutes ses sociétés,
// le bouton ▶ seul déplie. Le GIE n'est pas une société BI : il ne filtre
// pas, et il n'est retenu que si tout CENTRAUTODIS l'est.
//
// Filtre d'activité (2026-10-28) : clé `activites` de l'URL, reflétée dans le
// panneau. Un clic sur une case concession × activité pose la concession ET
// l'activité ; le bandeau et la liste des collaborateurs les suivent. Le bloc
// par activité garde toutes ses colonnes (un bloc ne se filtre pas lui-même).
//
// Coût employeur = brut + cotisations patronales. La « masse » de Finance
// ajoute le non soumis (indemnités, frais) : montrée à part, elle n'entre pas
// dans les ratios.

const ACT_LIB = { VN: 'VN', VO: 'VO', APV: 'APV', PR: 'PR', ADMIN: 'Admin' };
const MESURES_ENT = [
  { c: 'cout', l: 'Coût employeur' },
  { c: 'effectif', l: 'Effectif moyen' },
  { c: 'moyen', l: 'Coût moyen / salarié' },
];
const PRODUCTIVITE = [
  { c: 'VN', l: 'VN' }, { c: 'VO', l: 'VO' }, { c: 'APV', l: 'APV' }, { c: 'PR', l: 'PR' },
];
const somme = (a) => a.reduce((x, y) => x + y, 0);
const div = (a, b) => (b ? a / b : null);

// Agrégat d'une liste de lignes de paie. L'effectif MOYEN = somme des
// effectifs mensuels ÷ nombre de mois (un salarié présent 3 mois sur 6 compte
// pour 0,5) : on ne somme jamais des effectifs de mois différents.
function agreger(lignes, nbMois) {
  const t = { cout: 0, masse: 0, brut: 0, variable: 0, primes: 0, nonSoumis: 0, effMois: 0 };
  lignes.forEach((l) => {
    t.cout += l.cout; t.masse += l.masse; t.brut += l.brut; t.variable += l.variable;
    t.primes += l.primes; t.nonSoumis += l.nonSoumis; t.effMois += l.effectif;
  });
  t.effectif = nbMois ? t.effMois / nbMois : 0;
  t.moyen = div(t.cout, t.effMois);  // coût mensuel moyen par salarié
  return t;
}
const fmtEff = (v) => (v == null ? '—' : v.toLocaleString('fr-FR', { maximumFractionDigits: 1 }));

// ─── Collaborateurs de la sélection (2026-10-02) ────────────────────────────
// Pour faire le point avec une équipe : qui a été payé le dernier mois de paie
// de la période, sans aucun montant. N'apparaît que lorsqu'une concession est
// retenue (clic dans le bloc ou panneau) : la liste du groupe entier n'aide
// personne et ferait partir tous les noms à chaque ouverture. Le nombre de
// personnes doit retomber sur l'Effectif du bandeau.
const ancienneteDe = (iso) => {
  if (!iso) return '';
  const ans = (Date.now() - new Date(`${iso}T12:00:00`).getTime()) / (365.25 * 86400000);
  return ans < 1 ? `${Math.max(0, Math.round(ans * 12))} mois` : `${Math.floor(ans)} an${Math.floor(ans) > 1 ? 's' : ''}`;
};
function Collaborateurs({ entites, periode, moisStr, plusieursSocietes, acts, setActs, sel, onFermerSel }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  // Le sélecteur d'activité du bloc écrit le filtre `activites` de la page
  // (2026-10-28) : avant, c'était un état propre au bloc, que ni le clic sur
  // une case ni le panneau ne pilotaient.
  const cle = entites.join('|');
  useEffect(() => {
    let actif = true; setData(null); setErreur('');
    api.collaborateurs({ entites: cle, periode, mois: moisStr, ym: sel?.ym || undefined })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Liste indisponible'); });
    return () => { actif = false; };
  }, [cle, periode, moisStr, sel?.ym]);
  const tous = data?.collaborateurs || [];
  const activites = ['VN', 'VO', 'APV', 'PR', 'ADMIN', ...[...new Set(tous.map((c) => c.activite))].filter((a) => !ACT_LIB[a])]
    .filter((a) => tous.some((c) => c.activite === a) || acts.includes(a));
  // Case du bloc Effectifs par service (2026-10-29) : le service, voire l'intitulé exact.
  const memePoste = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
  const liste = tous.filter((c) => (!acts.length || acts.includes(c.activite))
    && (!sel?.service || c.serviceCle === sel.service) && (!sel?.poste || memePoste(c.poste, sel.poste)));
  const titreActs = [sel?.poste || sel?.service, ...acts.map((a) => ACT_LIB[a] || a)].filter(Boolean).join(' · ');
  return (
    <div className="vt-card">
      <div className="vt-card-h">
        <h2>Collaborateurs{titreActs ? ` — ${titreActs}` : ''}</h2>
        <div className="st-tools">
          <span className="hint">
            {data?.mois ? `${nb(liste.length)} personne${liste.length > 1 ? 's' : ''} payée${liste.length > 1 ? 's' : ''} en ${moisCourt(data.mois)} ${data.mois.slice(0, 4)}` : ''}
          </span>
          {sel && <button type="button" className="rh-puce" onClick={onFermerSel} title="Revenir à toute l'équipe">✕ {sel.poste || sel.service}</button>}
          {activites.length > 1 && (
            <span className="st-seg">
              <button type="button" className={!acts.length ? 'on' : ''} onClick={() => setActs([])}>Toutes</button>
              {activites.map((a) => <button key={a} type="button" className={acts.includes(a) ? 'on' : ''} onClick={() => setActs(acts.length === 1 && acts[0] === a ? [] : [a])}>{ACT_LIB[a] || a}</button>)}
            </span>
          )}
        </div>
      </div>
      <div className="vt-card-b vt-tbl-wrap">
        {erreur ? <p className="st-note">{erreur}</p>
          : !data ? <div className="vt-skel" />
          : data.indisponible ? <p className="st-note">Liste non encore alimentée par l'entrepôt.</p>
          : !liste.length ? <p className="st-note">Aucun collaborateur payé sur cette période{titreActs ? ` en ${titreActs}` : ''}.</p>
          : (
            <table className="vt-tbl rh-collab">
              <thead><tr>
                <th className="l">Collaborateur</th>
                {plusieursSocietes && <th className="l">Société</th>}
                <th className="l">Activité</th><th className="l">Poste</th><th className="l">Service</th><th>Entrée</th>
              </tr></thead>
              <tbody>
                {liste.map((c, i) => (
                  <tr key={`${c.entite}|${c.nom}|${c.prenom}|${i}`}>
                    <td className="l">{[c.nom, c.prenom].filter(Boolean).join(' ') || '—'}</td>
                    {plusieursSocietes && <td className="l">{c.societe}</td>}
                    <td className="l">{ACT_LIB[c.activite] || c.activite}</td>
                    <td className="l">{c.poste || '—'}</td>
                    <td className="l">{c.service || '—'}</td>
                    <td title={c.anciennete ? new Date(`${c.anciennete}T12:00:00`).toLocaleDateString('fr-FR') : ''}>{c.anciennete ? `${new Date(`${c.anciennete}T12:00:00`).toLocaleDateString('fr-FR')} · ${ancienneteDe(c.anciennete)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

export default function RhMasseSalariale() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [mesureEnt, setMesureEnt] = useState('cout');
  const [prod, setProd] = useState('VN');
  // Effectifs par service (2026-10-29) : services dépliés, case choisie (service,
  // intitulé, mois) — la liste des collaborateurs la suit.
  const [servOuverts, setServOuverts] = useState(() => new Set());
  const [selCase, setSelCase] = useState(null);
  const [unite, setUnite] = useState('k');
  const [ouverts, setOuverts] = useState(() => new Set());
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);

  useEffect(() => {
    let actif = true; setErreur('');
    api.masseSalariale({ periode: f.periode, mois: f.moisStr })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [f.periode, f.moisStr]);

  // Filtre d'activité : liste dans l'URL, vide = toutes.
  const actsUrl = f.params.get('activites') || '';
  const acts = useMemo(() => actsUrl.split(',').filter(Boolean), [actsUrl]);
  const setActs = (l) => f.setFiltre({ activites: l.join(',') });
  const groupes = useMemo(() => data?.groupes || [], [data]);
  const mois = useMemo(() => data?.moisPaie || [], [data]);
  // Sociétés de paie retenues par le filtre du panneau.
  const retenues = useMemo(() => groupes.flatMap((g) => g.entites.filter((e) => (e.societes.length
    ? e.societes.some((c) => f.selected.includes(c))
    : g.societes.every((c) => f.selected.includes(c)))).map((e) => e.entite)), [groupes, f.selected]);

  const actsDispo = data?.activites || [];
  const toggleAct = (a) => {
    const base = acts.length ? acts : actsDispo;
    const l = base.includes(a) ? base.filter((x) => x !== a) : [...base, a];
    setActs(l.length === actsDispo.length ? [] : l.length ? actsDispo.filter((x) => l.includes(x)) : [a]);
  };
  const Panneau = <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo}
    extra={{ categoriesDispo: actsDispo.map((a) => ({ code: a, nom: ACT_LIB[a] || a })), categoriesActives: acts.length ? acts : actsDispo,
      onToggleCategorie: toggleAct, titreCategories: 'Activité' }} />;
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;
  if (!groupes.length) {
    return <><main className="vt-page"><div className="bi-empty"><h2>Aucune masse salariale dans votre périmètre</h2><p>La paie apparaît ici pour les concessions dont toutes les sociétés sont dans votre habilitation.</p></div></main>{Panneau}</>;
  }

  const nbMois = mois.length;
  const lignes = data.lignes;
  const lignesDe = (ent, act) => lignes.filter((l) => (!ent || ent.includes(l.entite)) && (!act || l.activite === act));
  const dansActs = (l) => !acts.length || acts.includes(l.activite);
  const activiteDe = (ent) => {
    const t = { vn: 0, vo: 0, caMo: 0, caPieces: 0 };
    data.activite.filter((a) => ent.includes(a.entite)).forEach((a) => { t.vn += a.vn; t.vo += a.vo; t.caMo += a.caMo; t.caPieces += a.caPieces; });
    return t;
  };
  const fmt = (v) => (v == null ? '—' : montant(v, unite));
  // Montants UNITAIRES (par salarié, par véhicule) : toujours à l'euro — « 1 k€ »
  // par VN vendu ne distingue pas 600 € de 1 400 €.
  const unit = (v) => (v == null ? '—' : eur(v));

  // ── L'arbre : concession groupée ▸ sociétés de paie ──
  // Une concession à une seule société s'affiche directement sous le nom de la
  // société (règle de regroupements.js). Une ligne de groupe additionne ses
  // seules sociétés retenues.
  const basculerGroupe = (g) => setOuverts((o) => { const x = new Set(o); x.has(g) ? x.delete(g) : x.add(g); return x; });
  const arbre = [];
  groupes.forEach((g) => {
    const membres = g.entites.filter((e) => retenues.includes(e.entite));
    if (!membres.length) return;
    if (g.entites.length === 1) { arbre.push({ cle: g.groupe, nom: membres[0].nom, ent: [membres[0].entite], feuille: membres[0], niveau: 0 }); return; }
    const on = ouverts.has(g.groupe);
    arbre.push({ cle: g.groupe, nom: g.groupe, ent: membres.map((e) => e.entite), groupe: g, on, nb: membres.length, niveau: 0 });
    if (on) membres.forEach((e) => arbre.push({ cle: `${g.groupe}/${e.entite}`, nom: e.nom, ent: [e.entite], feuille: e, niveau: 1 }));
  });
  // Une sélection se compare en ENSEMBLE : le sélecteur de concession groupée
  // de la barre latérale n'écrit pas forcément les codes dans le même ordre.
  const memes = (url, codes) => { const a = url.split(',').filter(Boolean).sort().join(','); return !!a && a === [...codes].sort().join(','); };
  const cibleDe = (r) => (r.groupe ? r.groupe.societes : (r.feuille?.societes || []));
  const choisir = (codes) => f.setFiltre({ societes: memes(f.societesUrl, codes) ? '' : codes.join(',') });
  // Case concession × activité : pose les deux ; recliquer la case retenue annule les deux.
  const choisirCase = (r, a) => {
    const codes = cibleDe(r);
    if (!codes.length) return;
    if (memes(f.societesUrl, codes) && acts.length === 1 && acts[0] === a) f.setFiltre({ societes: '', activites: '' });
    else f.setFiltre({ societes: codes.join(','), activites: a });
  };
  const caseActive = (r, a) => cibleDe(r).length > 0 && memes(f.societesUrl, cibleDe(r)) && acts.length === 1 && acts[0] === a;
  // Cellule de libellé commune aux trois tableaux.
  const Libelle = ({ r }) => (
    <td className={`l${r.groupe ? ' rh-grp' : ''}`} style={r.niveau ? { paddingLeft: 30 } : undefined}>
      {r.groupe ? (
        <button type="button" className={`rg-btn${r.on ? ' on' : ''}`} title={r.on ? 'Replier' : 'Voir ses sociétés'} aria-label="Déplier"
          onClick={(e) => { e.stopPropagation(); basculerGroupe(r.cle); }}>▶</button>
      ) : !r.niveau ? <span className="rg-chev vide" /> : null}{r.nom}
      {r.groupe ? <span className="rh-nb">{r.nb} société{r.nb > 1 ? 's' : ''}</span> : null}
    </td>
  );
  const propsLigne = (r) => {
    if (r.groupe) {
      const actif = memes(f.societesUrl, r.groupe.societes);
      return { className: `tbl-clic rh-l-grp${actif ? ' actif' : ''}`, onClick: () => choisir(r.groupe.societes), title: 'Filtrer sur cette concession groupée' };
    }
    if (r.feuille && r.feuille.societes.length) {
      const actif = memes(f.societesUrl, r.feuille.societes);
      return { className: `tbl-clic${actif ? ' actif' : ''}${r.niveau ? ' rh-l-sub' : ''}`, onClick: () => choisir(r.feuille.societes), title: 'Filtrer sur cette société' };
    }
    return { className: r.niveau ? 'rh-l-sub' : undefined };
  };
  const plusieurs = arbre.filter((r) => r.niveau === 0).length > 1 || arbre.some((r) => r.groupe);

  // ── Bandeau ──
  const tot = agreger(lignesDe(retenues), nbMois);
  const totK = agreger(lignesDe(retenues).filter(dansActs), nbMois);
  const dernier = mois[mois.length - 1];
  const effDernier = somme(lignes.filter((l) => retenues.includes(l.entite) && l.ym === dernier && dansActs(l)).map((l) => l.effectif));
  const apvTot = agreger(lignesDe(retenues, 'APV'), nbMois);
  const actTot = activiteDe(retenues);
  const K = [
    { l: 'Effectif', v: nb(effDernier), d: dernier ? `paie de ${moisCourt(dernier)} ${dernier.slice(0, 4)}${acts.length ? ` · ${acts.map((a) => ACT_LIB[a] || a).join(', ')}` : ''}` : '—' },
    { l: 'Coût employeur', v: eurCourt(totK.cout), d: `${nbMois} mois de paie` },
    { l: 'Coût moyen / salarié', v: eur(totK.moyen || 0), d: 'par mois' },
    { l: 'Part variable', v: pct1(div(totK.variable, totK.brut) != null ? div(totK.variable, totK.brut) * 100 : null), d: 'du brut' },
    { l: 'Coût APV / CA M.O.', v: pct1(div(apvTot.cout, actTot.caMo) != null ? div(apvTot.cout, actTot.caMo) * 100 : null), d: `CA M.O. ${eurCourt(actTot.caMo)}` },
  ];

  // ── Bloc 1 : entité × activité ──
  const valEnt = (t) => (mesureEnt === 'cout' ? fmt(t.cout) : mesureEnt === 'effectif' ? fmtEff(t.effectif) : unit(t.moyen));

  // ── Bloc 2 : productivité par activité ──
  const colsProd = {
    VN: { h: ['VN vendus', 'Effectif VN moyen', 'VN / salarié / mois', 'Coût VN', 'Coût par VN vendu'],
      v: (t, a) => [nb(a.vn), fmtEff(t.effectif), fmtEff(div(a.vn, t.effMois)), fmt(t.cout), unit(div(t.cout, a.vn))] },
    VO: { h: ['VO vendus', 'Effectif VO moyen', 'VO / salarié / mois', 'Coût VO', 'Coût par VO vendu'],
      v: (t, a) => [nb(a.vo), fmtEff(t.effectif), fmtEff(div(a.vo, t.effMois)), fmt(t.cout), unit(div(t.cout, a.vo))] },
    APV: { h: ['CA M.O.', 'Effectif APV moyen', 'CA M.O. / salarié / mois', 'Coût APV', 'Coût / CA M.O.'],
      v: (t, a) => [fmt(a.caMo), fmtEff(t.effectif), unit(div(a.caMo, t.effMois)), fmt(t.cout), a.caMo ? pct1((t.cout / a.caMo) * 100) : '—'] },
    PR: { h: ['CA pièces', 'Effectif PR moyen', 'CA pièces / salarié / mois', 'Coût PR', 'Coût / CA pièces'],
      v: (t, a) => [fmt(a.caPieces), fmtEff(t.effectif), unit(div(a.caPieces, t.effMois)), fmt(t.cout), a.caPieces ? pct1((t.cout / a.caPieces) * 100) : '—'] },
  }[prod];

  // ── Une concession choisie ? (2026-10-29, schéma des autres onglets) ──
  // Le bloc 1 sert à choisir : sans choix, les blocs du dessous comparent les
  // concessions ; une concession (ou une de ses sociétés) choisie, ils la
  // détaillent — productivité activité par activité, effectifs mois par mois.
  const groupesRetenus = groupes.filter((g) => g.entites.some((e) => retenues.includes(e.entite)));
  const uneConc = !!f.societesUrl && groupesRetenus.length === 1;
  const nomConc = !uneConc ? '' : retenues.length === 1
    ? (groupesRetenus[0].entites.find((e) => e.entite === retenues[0])?.nom || retenues[0]) : groupesRetenus[0].groupe;

  // ── Bloc 3 : effectifs par poste ──
  // Des NOMBRES de personnes payées (marts.effectifs, sans nom) par SERVICE de
  // paie (2026-10-29 : le user préfère le service, tel que saisi, aux familles
  // de poste proposées le même jour). Rangés par activité puis par effectif.
  // Comparaison : services × concessions, au dernier mois de paie de la
  // période ; une concession choisie : services × mois, puis l'écart.
  const postesSel = (data.postes || []).filter((p) => retenues.includes(p.entite) && dansActs(p));
  const nbPostes = (filtre) => somme(postesSel.filter(filtre).map((p) => p.n));
  const colsComp = arbre.filter((r) => r.niveau === 0);
  const ORDRE_ACT = ['VN', 'VO', 'APV', 'PR', 'ADMIN'];
  const actDu = (sv) => { const c = {}; postesSel.filter((p) => p.service === sv).forEach((p) => { c[p.activite] = (c[p.activite] || 0) + p.n; }); return Object.entries(c).sort((a, b) => b[1] - a[1])[0]?.[0]; };
  const rangAct = (a) => { const i = ORDRE_ACT.indexOf(a); return i < 0 ? 99 : i; };
  const services = [...new Set(postesSel.filter((p) => p.n).map((p) => p.service))].map((sv) => ({ sv, a: actDu(sv), n: nbPostes((p) => p.service === sv && p.ym === dernier) }))
    .sort((x, y) => rangAct(x.a) - rangAct(y.a) || y.n - x.n || x.sv.localeCompare(y.sv, 'fr')).map((x) => x.sv);
  const intitulesDe = (fa) => [...new Set(postesSel.filter((p) => p.service === fa).map((p) => p.poste))]
    .sort((a, b) => nbPostes((p) => p.poste === b && p.ym === dernier) - nbPostes((p) => p.poste === a && p.ym === dernier) || a.localeCompare(b, 'fr'));
  const basculerServ = (fa) => setServOuverts((o) => { const x = new Set(o); x.has(fa) ? x.delete(fa) : x.add(fa); return x; });
  // La case retenue ne vaut que pour la sélection où elle a été posée.
  const sel = selCase && memes(f.societesUrl, selCase.cible) ? selCase : null;
  // Sans mois (case posée depuis la comparaison), la liste est celle du dernier mois : c'est cette case qui s'allume.
  const estSel = (fa, poste, ym) => !!sel && sel.service === fa && (sel.poste || null) === (poste || null) && (sel.ym || dernier) === ym;
  // Comparaison : la case choisit la concession ET le service (la page passe en vue concession).
  const choisirCaseComp = (r, fa, poste) => {
    const codes = cibleDe(r); if (!codes.length) return;
    f.setFiltre({ societes: codes.join(',') });
    setSelCase({ service: fa, poste: poste || null, ym: null, cible: codes });
  };
  // Concession : la case (service ou intitulé × mois) filtre la liste ; recliquer annule.
  const choisirCaseMois = (fa, poste, ym) => setSelCase(estSel(fa, poste, ym) ? null
    : { service: fa, poste: poste || null, ym, cible: f.societesUrl.split(',').filter(Boolean) });
  const ecartTxt = (e) => (e > 0 ? `+${nb(e)}` : e < 0 ? `−${nb(-e)}` : '0');
  const ecartCl = (e) => (e > 0 ? 'tbl-pos' : e < 0 ? 'tbl-neg' : '');

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>RH — Masse salariale</h1></div>
          <div className="vt-asof"><b>{data.periode}</b>{nbMois ? <><br /><span>paie {moisCourt(mois[0])} → {moisCourt(dernier)} {dernier.slice(0, 4)}</span></> : null}</div>
        </div>

        {!nbMois ? (
          <div className="bi-empty"><h2>Aucune paie importée sur cette période</h2><p>Dernier mois de paie importé dans Finance : {data.dernierMoisImporte ? `${moisCourt(data.dernierMoisImporte)} ${data.dernierMoisImporte.slice(0, 4)}` : '—'}.</p></div>
        ) : retenues.length === 0 ? (
          <div className="bi-empty"><h2>Aucune société sélectionnée</h2><p>Cochez au moins une société dans les filtres à droite.</p></div>
        ) : (
          <>
            <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
              {K.map((k) => (
                <div className="vt-kpi" key={k.l}><div className="l">{k.l}</div><div className="v">{k.v}</div><div className="d"><small>{k.d}</small></div></div>
              ))}
            </div>

            {/* ── Bloc 1 : entité × activité ── */}
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Par concession et par activité</h2>
                <div className="st-tools">
                  <span className="hint">coût employeur = brut + cotisations patronales</span>
                  <span className="st-seg">{MESURES_ENT.map((m) => (
                    <button key={m.c} type="button" className={mesureEnt === m.c ? 'on' : ''} onClick={() => setMesureEnt(m.c)}>{m.l}</button>
                  ))}</span>
                  {mesureEnt === 'cout' && <SelecteurUnite unite={unite} setUnite={setUnite} />}
                </div>
              </div>
              <div className="vt-card-b vt-tbl-wrap">
                <table className="vt-tbl">
                  <thead><tr>
                    <th className="l">Concession</th>
                    {data.activites.map((a) => <th key={a}>{ACT_LIB[a] || a}</th>)}
                    <th className="tot">Total</th><th>Non soumis</th>
                  </tr></thead>
                  <tbody>
                    {arbre.map((r) => {
                      const t = agreger(lignesDe(r.ent), nbMois);
                      return (
                        <tr key={r.cle} {...propsLigne(r)}>
                          <Libelle r={r} />
                          {data.activites.map((a) => (
                            <td key={a} className={cibleDe(r).length ? `rh-case${caseActive(r, a) ? ' on' : ''}` : undefined}
                              title={cibleDe(r).length ? `${r.nom} · ${ACT_LIB[a] || a} : filtrer la page` : undefined}
                              onClick={cibleDe(r).length ? (e) => { e.stopPropagation(); choisirCase(r, a); } : undefined}>
                              {valEnt(agreger(lignesDe(r.ent, a), nbMois))}
                            </td>
                          ))}
                          <td className="tot">{valEnt(t)}</td>
                          <td>{fmt(t.nonSoumis)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {plusieurs && (
                    <tfoot><tr>
                      <td className="l">Total</td>
                      {data.activites.map((a) => <td key={a}>{valEnt(agreger(lignesDe(retenues, a), nbMois))}</td>)}
                      <td className="tot">{valEnt(tot)}</td>
                      <td>{fmt(tot.nonSoumis)}</td>
                    </tr></tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* ── Bloc 2 : productivité et poids salarial ──
                Concession choisie : une ligne par activité, sans sélecteur. */}
            {uneConc ? (
              <div className="vt-card">
                <div className="vt-card-h">
                  <h2>Productivité et poids salarial — {nomConc}</h2>
                  <div className="st-tools">
                    <span className="hint">sur les {nbMois} mois de paie importés · effectif moyen · ventes nettes ; CA M.O. et CA pièces au sens d'APV et PR — Performance</span>
                  </div>
                </div>
                <div className="vt-card-b vt-tbl-wrap">
                  <table className="vt-tbl">
                    <thead><tr><th className="l">Activité</th><th>Ventes</th><th>Effectif moyen</th><th>Par salarié / mois</th><th>Coût employeur</th><th>Coût rapporté aux ventes</th></tr></thead>
                    <tbody>
                      {['VN', 'VO', 'APV', 'PR', 'ADMIN'].map((a) => {
                        const t = agreger(lignesDe(retenues, a), nbMois); const v = activiteDe(retenues);
                        const ventes = { VN: v.vn, VO: v.vo, APV: v.caMo, PR: v.caPieces }[a];
                        const cells = a === 'VN' || a === 'VO'
                          ? [`${nb(ventes)} ${a}`, fmtEff(t.effectif), div(ventes, t.effMois) == null ? '—' : `${fmtEff(div(ventes, t.effMois))} ${a}`, fmt(t.cout), div(t.cout, ventes) == null ? '—' : `${unit(div(t.cout, ventes))} par ${a}`]
                          : a === 'ADMIN' ? ['—', fmtEff(t.effectif), '—', fmt(t.cout), '—']
                            : [`${fmt(ventes)} ${a === 'APV' ? 'de M.O.' : 'de pièces'}`, fmtEff(t.effectif), unit(div(ventes, t.effMois)), fmt(t.cout),
                              ventes ? `${pct1((t.cout / ventes) * 100)} du CA ${a === 'APV' ? 'M.O.' : 'pièces'}` : '—'];
                        return <tr key={a}><td className="l"><b>{ACT_LIB[a]}</b></td>{cells.map((c, i) => <td key={i}>{c}</td>)}</tr>;
                      })}
                    </tbody>
                    <tfoot><tr><td className="l">Total</td><td /><td>{fmtEff(agreger(lignesDe(retenues), nbMois).effectif)}</td><td /><td>{fmt(agreger(lignesDe(retenues), nbMois).cout)}</td><td /></tr></tfoot>
                  </table>
                </div>
              </div>
            ) : (
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Productivité et poids salarial — {prod}</h2>
                <div className="st-tools">
                  <span className="hint">
                    {prod === 'APV' ? 'CA M.O. = définition d\'APV — Performance' : prod === 'PR' ? 'CA pièces = comptoir + atelier (PR — Performance)' : 'ventes nettes (annulations déduites)'}
                    {' · '}sur les {nbMois} mois de paie importés{groupes.some((g) => g.entites.some((e) => !e.codsocs.length)) ? ' · GIE : personnel partagé, sans vente propre' : ''}
                  </span>
                  <span className="st-seg">{PRODUCTIVITE.map((p) => (
                    <button key={p.c} type="button" className={prod === p.c ? 'on' : ''} onClick={() => setProd(p.c)}>{p.l}</button>
                  ))}</span>
                </div>
              </div>
              <div className="vt-card-b vt-tbl-wrap">
                <table className="vt-tbl">
                  <thead><tr><th className="l">Concession</th>{colsProd.h.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {arbre.map((r) => (
                      <tr key={r.cle} {...propsLigne(r)}><Libelle r={r} />{colsProd.v(agreger(lignesDe(r.ent, prod), nbMois), activiteDe(r.ent)).map((v, i) => <td key={i}>{v}</td>)}</tr>
                    ))}
                  </tbody>
                  {plusieurs && (
                    <tfoot><tr><td className="l">Total</td>{colsProd.v(agreger(lignesDe(retenues, prod), nbMois), activiteDe(retenues)).map((v, i) => <td key={i}>{v}</td>)}</tr></tfoot>
                  )}
                </table>
              </div>
            </div>
            )}

            {/* ── Bloc 3 : effectifs par service (2026-10-29) ── */}
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Effectifs par service{uneConc ? ` — ${nomConc}` : ''}</h2>
                <div className="st-tools">
                  <span className="hint">
                    {uneConc ? 'personnes payées chaque mois' : `personnes payées en ${dernier ? `${moisCourt(dernier)} ${dernier.slice(0, 4)}` : '—'}`}
                    {' · '}▶ les intitulés de poste · clic sur un chiffre : les collaborateurs{acts.length ? ` · ${acts.map((a) => ACT_LIB[a] || a).join(', ')}` : ''}
                  </span>
                </div>
              </div>
              <div className="vt-card-b vt-tbl-wrap">
                {!services.length ? <p className="st-note">Aucun effectif par service sur cette période (liste des collaborateurs non alimentée).</p> : (
                  <table className="vt-tbl rh-postes">
                    <thead><tr>
                      <th className="l">Service</th>
                      {uneConc ? <>{mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}{nbMois > 1 && <th className="tot">Écart</th>}</>
                        : <>{colsComp.map((r) => <th key={r.cle}>{r.nom}</th>)}<th className="tot">Total</th></>}
                    </tr></thead>
                    <tbody>
                      {services.flatMap((fa) => {
                        const ouverte = servOuverts.has(fa);
                        const lignesServ = [{ fa, poste: null }, ...(ouverte ? intitulesDe(fa).map((po) => ({ fa, poste: po })) : [])];
                        return lignesServ.map(({ poste }) => {
                          const filtre = (p) => p.service === fa && (!poste || p.poste === poste);
                          return (
                            <tr key={`${fa}|${poste || ''}`} className={poste ? 'rh-l-sub' : undefined}>
                              <td className="l" style={poste ? { paddingLeft: 40 } : undefined}>
                                {!poste && <button type="button" className={`rg-btn${ouverte ? ' on' : ''}`} onClick={() => basculerServ(fa)}
                                  title={ouverte ? 'Replier' : 'Voir les intitulés de poste'} aria-label="Déplier">▶</button>}
                                {poste || fa}
                              </td>
                              {uneConc ? (
                                <>
                                  {mois.map((m) => {
                                    const v = nbPostes((p) => filtre(p) && p.ym === m);
                                    return <td key={m} className={`rh-case${estSel(fa, poste, m) ? ' on' : ''}`} onClick={() => choisirCaseMois(fa, poste, m)}
                                      title={`${poste || fa} · ${moisCourt(m)} : voir les collaborateurs`}>{v ? nb(v) : '·'}</td>;
                                  })}
                                  {nbMois > 1 && (() => {
                                    const e = nbPostes((p) => filtre(p) && p.ym === dernier) - nbPostes((p) => filtre(p) && p.ym === mois[0]);
                                    return <td className={`tot ${ecartCl(e)}`} title={`${moisCourt(mois[0])} → ${moisCourt(dernier)}`}>{ecartTxt(e)}</td>;
                                  })()}
                                </>
                              ) : (
                                <>
                                  {colsComp.map((r) => {
                                    const v = nbPostes((p) => filtre(p) && p.ym === dernier && r.ent.includes(p.entite));
                                    return <td key={r.cle} className={cibleDe(r).length ? 'rh-case' : undefined}
                                      onClick={cibleDe(r).length ? () => choisirCaseComp(r, fa, poste) : undefined}
                                      title={cibleDe(r).length ? `${r.nom} · ${poste || fa} : voir la concession et ses collaborateurs` : undefined}>{v ? nb(v) : '·'}</td>;
                                  })}
                                  <td className="tot">{nb(nbPostes((p) => filtre(p) && p.ym === dernier))}</td>
                                </>
                              )}
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                    <tfoot><tr>
                      <td className="l">Total</td>
                      {uneConc ? <>{mois.map((m) => <td key={m}>{nb(nbPostes((p) => p.ym === m))}</td>)}
                        {nbMois > 1 && (() => { const e = nbPostes((p) => p.ym === dernier) - nbPostes((p) => p.ym === mois[0]); return <td className={`tot ${ecartCl(e)}`}>{ecartTxt(e)}</td>; })()}</>
                        : <>{colsComp.map((r) => <td key={r.cle}>{nb(nbPostes((p) => p.ym === dernier && r.ent.includes(p.entite)))}</td>)}<td className="tot">{nb(nbPostes((p) => p.ym === dernier))}</td></>}
                    </tr></tfoot>
                  </table>
                )}
              </div>
            </div>

            {/* ── Collaborateurs : sous les effectifs, seulement quand une concession est retenue ── */}
            {f.societesUrl && retenues.length > 0 && (
              <Collaborateurs entites={retenues} periode={f.periode} moisStr={f.moisStr} plusieursSocietes={retenues.length > 1} acts={acts} setActs={setActs}
                sel={sel} onFermerSel={() => setSelCase(null)} />
            )}
          </>
        )}
      </main>
      {Panneau}
    </>
  );
}
